import type { ApiSchemas } from '@rental/contracts/client'
import {
  canMoveFine,
  type FineStatus,
  isPlateKey,
  plateKey,
  type StaffLocale,
} from '@rental/domain'
import { plural, type StaffMessages } from '@rental/i18n'
import type { ChipTone } from '../components/StatusChip'
import { centsForInput, parseEuroCents } from '../money'

/**
 * S6 (CMP-2, decision 4): what the fines screens decide by themselves, which
 * is only words, tones and the shape of what is typed. Every move is the
 * server's to allow; the buttons offered are the domain's own transitions.
 */

/** The screens that show fines: the register, one fine, a booking's or a customer's. */
export const FINES_QUERY_KEY = ['fines'] as const

type Deadline = ApiSchemas['FineDeadline']

/** How near the deadline to name the renter is, in words; or what became of it. */
export function deadlineWords(deadline: Deadline, locale: StaffLocale, m: StaffMessages): string {
  const f = m.fines
  if (deadline.state === 'met' || deadline.state === 'late' || deadline.state === 'none') {
    return f.deadline[deadline.state]
  }
  if (deadline.daysLeft > 0) return plural(locale, f.daysLeft, deadline.daysLeft)
  if (deadline.daysLeft === 0) return f.dueToday
  return plural(locale, f.daysPast, -deadline.daysLeft)
}

/**
 * Overdue: danger, someone must act; within a week: warning; still running:
 * information. One that has ended, in time or late, or that has none, is
 * neutral (UI-2g): nothing is left to do about it, as with the fine's status.
 */
export function deadlineTone(state: Deadline['state']): ChipTone {
  switch (state) {
    case 'overdue':
      return 'danger'
    case 'due_soon':
      return 'warning'
    case 'running':
      return 'info'
    default:
      return 'neutral'
  }
}

/**
 * Registered waits for the Owner; matched and communicated are on their way;
 * the rest is over, neutral (UI-2e): a notice ruled out or withdrawn is no
 * one's success, and green beside the open ones read as one more to look at.
 */
export function statusTone(status: FineStatus): ChipTone {
  switch (status) {
    case 'registered':
      return 'warning'
    case 'matched':
    case 'communicated':
      return 'info'
    default:
      return 'neutral'
  }
}

/** The notice as typed, field by field: text until it is sent. */
export interface NoticeDraft {
  authority: string
  noticeNumber: string
  violatedOn: string
  violatedAt: string
  place: string
  plate: string
  amount: string
  notifiedOn: string
}

export type NoticeField = keyof NoticeDraft

export const NOTICE_FIELDS: readonly NoticeField[] = [
  'authority',
  'noticeNumber',
  'violatedOn',
  'violatedAt',
  'place',
  'plate',
  'amount',
  'notifiedOn',
]

export function emptyNotice(): NoticeDraft {
  return {
    authority: '',
    noticeNumber: '',
    violatedOn: '',
    violatedAt: '',
    place: '',
    plate: '',
    amount: '',
    notifiedOn: '',
  }
}

/** A registered notice, back in the form to correct it. */
export function noticeDraftOf(
  fine: Pick<
    ApiSchemas['StaffFine'],
    | 'authority'
    | 'noticeNumber'
    | 'violatedOn'
    | 'violatedAt'
    | 'place'
    | 'plate'
    | 'amountCents'
    | 'notifiedOn'
  >,
  locale: StaffLocale,
): NoticeDraft {
  return {
    authority: fine.authority,
    noticeNumber: fine.noticeNumber,
    violatedOn: fine.violatedOn,
    violatedAt: fine.violatedAt,
    place: fine.place,
    plate: fine.plate,
    amount: centsForInput(fine.amountCents, locale),
    notifiedOn: fine.notifiedOn,
  }
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/
const HH_MM = /^([01]\d|2[0-3]):[0-5]\d$/
/** The largest notice the register takes: as the server's contract. */
const MAX_AMOUNT_CENTS = 10_000_000

/**
 * The notice to send, or the fields that are not plainly what the notice
 * prints. Only shapes: whether the dates make sense together, whether the
 * notice exists already and whether the plate is ours are the server's.
 */
export function noticeBody(
  draft: NoticeDraft,
): { ok: true; body: ApiSchemas['FineNotice'] } | { ok: false; invalid: NoticeField[] } {
  const invalid: NoticeField[] = []
  const authority = draft.authority.trim()
  const noticeNumber = draft.noticeNumber.trim()
  const place = draft.place.trim()
  // A time field may carry seconds; the notice prints the minute.
  const violatedAt = draft.violatedAt.trim().slice(0, 5)
  const amountCents = parseEuroCents(draft.amount)
  if (authority.length < 2 || authority.length > 200) invalid.push('authority')
  if (noticeNumber.length < 1 || noticeNumber.length > 60) invalid.push('noticeNumber')
  if (!ISO_DATE.test(draft.violatedOn)) invalid.push('violatedOn')
  if (!HH_MM.test(violatedAt)) invalid.push('violatedAt')
  if (place.length < 2 || place.length > 300) invalid.push('place')
  if (draft.plate.trim().length > 20 || !isPlateKey(plateKey(draft.plate))) invalid.push('plate')
  if (amountCents === null || amountCents > MAX_AMOUNT_CENTS) invalid.push('amount')
  if (!ISO_DATE.test(draft.notifiedOn)) invalid.push('notifiedOn')
  if (invalid.length > 0 || amountCents === null) return { ok: false, invalid }
  return {
    ok: true,
    body: {
      authority,
      noticeNumber,
      violatedOn: draft.violatedOn,
      violatedAt,
      place,
      plate: draft.plate.trim(),
      amountCents,
      notifiedOn: draft.notifiedOn,
    },
  }
}

/** The ways a fine can end from where it is, as the domain allows them. */
export type FineEnding = 'not_ours' | 'withdrawn' | 'closed'

export function endingsFrom(status: FineStatus): FineEnding[] {
  return (['not_ours', 'withdrawn', 'closed'] as const).filter((to) => canMoveFine(status, to))
}

/** A reason is asked for every ending but closing a fine whose fee was charged. */
export function reasonRequired(
  to: FineEnding | 'unmatch' | 'reopen',
  feeCharged: boolean,
): boolean {
  return !(to === 'closed' && feeCharged)
}

/** The shortest reason the server takes. */
export const REASON_MIN = 3
